// The layout of the tree view: the root on the left, the subsystems in a
// column beside it, closed as one box each, and the open one as a box that
// holds its window: the path of open kinds above the window drawn small,
// then a column for each level the window shows, each next to the open kind
// of the level before. The tree of what the root is built into is the same
// layout mirrored, the root on the right: a host is always left of what it
// holds. Pure: the mapping is tested without a canvas.

import { ChainEdge, ChainSide } from "../../types/valueChain";
import {
  CARD_SIZE,
  COLLAPSED_SIZE,
  ChainFlowEdge,
  ChainNode,
  ChainNodeData,
  EdgeShape,
  EdgeTone,
  Position,
  Size,
} from "./chainLayout";
import { cardName, typicalOnly } from "./chainModel";
import {
  TreeCardInfo,
  TreePlacement,
  VisibleTree,
  childrenKey,
  placementKey,
  treeParentOf,
} from "./treeModel";

// The size of a tree card, of a card on the path and of a closed subsystem,
// before they are measured
export const TREE_CARD_SIZE: Size = { width: 300, height: 120 };
export const SPINE_SIZE: Size = { width: 220, height: 44 };
export const SUBSYSTEM_SIZE: Size = { width: 300, height: 64 };

// Space between the root and the subsystems, between two subsystems, between
// two cards of a level, between two levels and between two cards of the path
const ROOT_GAP = 120;
const BOX_GAP = 14;
const TREE_CARD_GAP = 16;
const TREE_COLUMN_GAP = 96;
const SPINE_GAP = 56;

// The open subsystem's box: its head, the room for the rail its first level
// hangs on, the space around its cards, and where the edge from the root
// meets it
export const BRANCH_HEAD = 52;
export const BRANCH_RAIL = 36;
const BRANCH_PAD = 20;
export const BRANCH_HANDLE_Y = 26;

// The ids of the nodes of a closed subsystem and of the open one's box
export const subsystemNodeId = (name: string): string => `subsystem:${name}`;
export const branchNodeId = (name: string): string => `branch:${name}`;

export interface TreeLayout {
  // The top-left corner of every node
  positions: Map<string, Position>;
  // The open subsystem's box; null when every one is closed
  branch: (Position & Size) | null;
  // Grown leftwards from the root: the tree of what it is built into, so
  // that a host is left of what it holds, as in the parts tree
  mirrored: boolean;
}

// Where the nodes of the tree view go, by the sizes measured so far. The
// tree of hosts is laid out as the parts tree is, then mirrored about the
// root's left edge.
export const treeLayout = (
  visible: VisibleTree,
  measured: ReadonlyMap<string, Size>,
): TreeLayout => {
  // The width of each node placed, for mirroring
  const widths = new Map<string, number>();
  const sizeOf = (id: string, fallback: Size): Size => {
    const size = measured.get(id) ?? fallback;
    widths.set(id, size.width);
    return size;
  };
  const { tree } = visible;
  const positions = new Map<string, Position>([[tree.root.id, { x: 0, y: 0 }]]);
  const left = sizeOf(tree.root.id, CARD_SIZE).width + ROOT_GAP;
  let branch: TreeLayout["branch"] = null;
  let y = 0;
  for (const subsystem of tree.subsystems) {
    if (subsystem.name !== visible.branch?.name) {
      const id = subsystemNodeId(subsystem.name);
      positions.set(id, { x: left, y });
      y += sizeOf(id, SUBSYSTEM_SIZE).height + BOX_GAP;
      continue;
    }
    const top = y + BRANCH_HEAD;
    let x = left + BRANCH_RAIL;
    let right = left + SUBSYSTEM_SIZE.width;
    let bottom = top;
    // The middle of the open kind of the level before, which the next
    // level is centred on
    let middle: number | null = null;
    for (const placement of visible.spine) {
      const size = sizeOf(placement.card.id, SPINE_SIZE);
      positions.set(placement.card.id, { x, y: top });
      middle = top + size.height / 2;
      right = x + size.width;
      bottom = Math.max(bottom, top + size.height);
      x += size.width + SPINE_GAP;
    }
    for (const column of visible.columns) {
      const ids = column.placements.map((placement) => placement.card.id);
      const sizes = [
        ...ids.map((id) => sizeOf(id, TREE_CARD_SIZE)),
        ...(column.collapsed ? [sizeOf(column.collapsed.id, COLLAPSED_SIZE)] : []),
      ];
      if (column.collapsed) {
        ids.push(column.collapsed.id);
      }
      const height =
        sizes.reduce((total, size) => total + size.height, 0) +
        Math.max(sizes.length - 1, 0) * TREE_CARD_GAP;
      let at = middle === null ? top : Math.max(top, middle - height / 2);
      let next: number | null = null;
      for (let index = 0; index < ids.length; index++) {
        positions.set(ids[index], { x, y: at });
        if (ids[index] === column.open) {
          next = at + sizes[index].height / 2;
        }
        at += sizes[index].height + TREE_CARD_GAP;
      }
      const width = Math.max(...sizes.map((size) => size.width));
      bottom = Math.max(bottom, at - TREE_CARD_GAP);
      right = x + width;
      middle = next;
      x += width + TREE_COLUMN_GAP;
    }
    branch = {
      x: left,
      y,
      width: right - left + BRANCH_PAD,
      height: bottom - y + BRANCH_PAD,
    };
    positions.set(branchNodeId(subsystem.name), { x: left, y });
    widths.set(branchNodeId(subsystem.name), branch.width);
    y += branch.height + BOX_GAP;
  }
  const mirrored = tree.side === "hosts";
  if (!mirrored) {
    return { positions, branch, mirrored };
  }
  const mirror = (at: Position, width: number): Position => ({
    x: -(at.x + width),
    y: at.y,
  });
  return {
    positions: new Map(
      Array.from(positions, ([id, at]): [string, Position] => [
        id,
        mirror(at, widths.get(id) ?? 0),
      ]),
    ),
    branch: branch && { ...branch, ...mirror(branch, branch.width) },
    mirrored,
  };
};

// The data of a card node; a tree card carries its place
const cardData = (
  placement: { card: TreePlacement["card"] },
  root: boolean,
  tree?: TreeCardInfo,
): ChainNodeData => ({
  variant: "card",
  card: placement.card,
  root,
  dimmed: false,
  pathColour: null,
  pathColours: new Map(),
  neighbours: [],
  compact: false,
  summary: null,
  partsDrawn: 0,
  ...(tree ? { tree } : {}),
});

// The nodes of the tree view at their positions, as the canvas takes them
export const placeTree = (
  visible: VisibleTree,
  layout: TreeLayout,
): ChainNode[] => {
  const { tree, branch, trail } = visible;
  const nodes: ChainNode[] = [];
  const mirrored = layout.mirrored ? { mirrored: true } : {};
  const place = (id: string, data: ChainNodeData) => {
    const position = layout.positions.get(id);
    if (position) {
      nodes.push({
        id,
        type: "chainCard",
        position,
        data: { ...data, ...mirrored },
      });
    }
  };
  if (branch && layout.branch) {
    nodes.push({
      id: branchNodeId(branch.name),
      type: "chainCard",
      position: { x: layout.branch.x, y: layout.branch.y },
      width: layout.branch.width,
      height: layout.branch.height,
      zIndex: -1,
      selectable: false,
      focusable: false,
      data: {
        variant: "branch",
        subsystem: branch,
        width: layout.branch.width,
        height: layout.branch.height,
        dimmed: false,
        ...mirrored,
      },
    });
  }
  place(tree.root.id, cardData({ card: tree.root }, true));
  for (const subsystem of tree.subsystems) {
    if (subsystem.name !== branch?.name) {
      place(subsystemNodeId(subsystem.name), {
        variant: "subsystem",
        subsystem,
        dimmed: false,
      });
    }
  }
  if (!branch) {
    return nodes;
  }

  const partsOf = (placement: TreePlacement): number =>
    placement.shared
      ? 0
      : (tree.children.get(childrenKey(branch.name, placement.card.id)) ?? [])
          .length;
  const elsewhere = (placement: TreePlacement): string[] =>
    placement.shared
      ? [tree.homes.get(placement.card.id)!.subsystem]
      : (tree.subsystemsOf.get(placement.card.id) ?? []).filter(
          (name) => name !== branch.name,
        );
  visible.spine.forEach((placement, level) =>
    place(
      placement.card.id,
      cardData(placement, false, {
        placement,
        side: tree.side,
        parts: partsOf(placement),
        open: true,
        spine: true,
        toggle: trail.slice(0, level + 1),
        elsewhere: elsewhere(placement),
      }),
    ),
  );
  for (const column of visible.columns) {
    for (const placement of column.placements) {
      const open = placement.card.id === column.open;
      place(
        placement.card.id,
        cardData(placement, false, {
          placement,
          side: tree.side,
          parts: partsOf(placement),
          open,
          spine: false,
          toggle: open
            ? trail.slice(0, column.level)
            : [...trail.slice(0, column.level), placement.card.id],
          elsewhere: elsewhere(placement),
        }),
      );
    }
    if (column.collapsed) {
      place(column.collapsed.id, {
        variant: "collapsed",
        row: column.collapsed,
        dimmed: false,
        rootName: cardName(tree.root),
      });
    }
  }
  return nodes;
};

// One edge of the tree view, standing for the stated edges given: none for
// an edge into a subsystem, which stands for its first level as a whole
const treeEdge = (
  id: string,
  source: string,
  target: string,
  edges: ChainEdge[],
  tone: EdgeTone,
  shape: EdgeShape = "stepped",
  collapsed: boolean = false,
  side: ChainSide = "parts",
): ChainFlowEdge => {
  const suspect = edges.some((edge) => edge.suspect);
  const typical = edges.length > 0 && edges.every(typicalOnly);
  return {
    id,
    source,
    target,
    type: "chainEdge",
    data: {
      drawn: {
        id,
        source,
        target,
        edges,
        collapsed,
        direct: false,
        suspect,
        typical,
        broken: false,
        pathModule: null,
        side,
      },
      shape,
      tone:
        tone === "plain" ? (suspect ? "suspect" : typical ? "typical" : tone) : tone,
      pathColour: null,
      dimmed: false,
      // The tree of hosts hangs each host on the part it holds, leftwards
      hostAtTarget: side === "hosts",
    },
  };
};

// The edges of the tree view: from the root into each subsystem, down the
// open subsystem's rail into its first level drawn, from each open kind into
// the kinds of the next level, and from another host on the canvas, nearer
// the root, into a kind it states too
export const toTreeEdges = (visible: VisibleTree): ChainFlowEdge[] => {
  const { tree, branch } = visible;
  const rootId = tree.root.id;
  const edges: ChainFlowEdge[] = tree.subsystems.map((subsystem) => {
    const target =
      subsystem.name === branch?.name
        ? branchNodeId(subsystem.name)
        : subsystemNodeId(subsystem.name);
    return treeEdge(
      `${rootId}→${target}`,
      rootId,
      target,
      [],
      subsystem.name === branch?.name ? "open" : "plain",
      "stepped",
      false,
      tree.side,
    );
  });
  if (!branch) {
    return edges;
  }

  const rail = branchNodeId(branch.name);
  let host: string | null = null;
  const into = (
    target: string,
    stated: ChainEdge[],
    tone: EdgeTone,
    collapsed = false,
  ) => {
    const source = host ?? rail;
    edges.push(
      treeEdge(
        `${source}→${target}`,
        source,
        target,
        stated,
        tone,
        host === null ? "rail" : "stepped",
        collapsed,
        tree.side,
      ),
    );
  };
  for (const placement of visible.spine) {
    into(placement.card.id, [placement.edge], "open");
    host = placement.card.id;
  }
  for (const column of visible.columns) {
    for (const placement of column.placements) {
      into(
        placement.card.id,
        [placement.edge],
        placement.card.id === column.open ? "open" : "plain",
      );
    }
    if (column.collapsed) {
      const hidden = (
        tree.children.get(childrenKey(branch.name, column.host)) ?? []
      ).filter((placement) => !column.placements.includes(placement));
      into(
        column.collapsed.id,
        hidden.map((placement) => placement.edge),
        "plain",
        true,
      );
    }
    if (!column.open) {
      break;
    }
    host = column.open;
  }

  // The column each drawn kind is in, the path's counted first
  const columnOf = new Map<string, number>();
  visible.spine.forEach((placement, index) =>
    columnOf.set(placement.card.id, index),
  );
  visible.columns.forEach((column, index) =>
    column.placements.forEach((placement) =>
      columnOf.set(placement.card.id, visible.spine.length + index),
    ),
  );
  columnOf.forEach((at, id) => {
    for (const edge of tree.crossEdges.get(placementKey(branch.name, id)) ?? []) {
      const parent = treeParentOf(tree.side, edge);
      const hostAt = columnOf.get(parent);
      if (hostAt !== undefined && hostAt < at) {
        edges.push(
          treeEdge(
            `cross:${parent}→${id}`,
            parent,
            id,
            [edge],
            "cross",
            "stepped",
            false,
            tree.side,
          ),
        );
      }
    }
  });
  return edges;
};
